<script setup>
import {computed, getCurrentInstance} from 'vue'
import {NodeSelection} from '@tiptap/pm/state'

const props = defineProps({
    editor: {
        type: Object,
        required: true
    }
})

const {proxy} = getCurrentInstance()
const store = proxy.$store

// --- Constants ---

const tagMap = {
    paragraph: 'p',
    heading: 'h',
    bulletList: 'ul',
    orderedList: 'ol',
    listItem: 'li',
    blockquote: 'blockquote',
    image: 'img',
    link: 'a',
    bold: 'strong',
    italic: 'em'
}

// --- Computed ---

const isDarkMode = computed(() => store.getters['user/pref']('darkMode'))

const breadcrumbs = computed(() => {
    if (!props.editor) return []

    const {selection, doc} = props.editor.state

    if (!props.editor.isFocused && selection.empty && selection.from <= 1) {
        return []
    }

    const trail = [{name: 'body', pos: 0, type: 'node', size: doc.nodeSize}]
    const isNode = selection instanceof NodeSelection
    const pos = isNode ? selection.from + 1 : selection.from

    // 1. Map Nodes
    doc.nodesBetween(pos, pos, (node, nodePos) => {
        if (node.type.name !== 'doc' && node.type.name !== 'text') {
            if (node.content.size === 0 && node.type.name !== 'image') {
                return
            }

            let name = tagMap[node.type.name] || node.type.name
            if (node.type.name === 'heading') name = `h${node.attrs.level}`

            trail.push({
                name: name,
                pos: nodePos,
                type: 'node',
                size: node.nodeSize
            })
        }
    })

    if (isNode && selection.node) {
        const node = selection.node
        if (node.type.name === 'image') {
            const name = tagMap[node.type.name] || 'img'
            if (trail[trail.length - 1]?.name !== name) {
                trail.push({
                    name: name,
                    pos: selection.from,
                    type: 'node',
                    size: node.nodeSize
                })
            }
        }
    }

    // 2. Map Marks
    const isNodeSelection = selection instanceof NodeSelection

    if (!isNodeSelection) {
        const {from, to, $from} = selection
        const isSelectingWholeBlock = (to - from) >= (doc.nodeAt(from)?.nodeSize || 0) - 2

        if (!isSelectingWholeBlock) {
            const activeMarks = []

            if (from !== to) {
                doc.nodesBetween(from, to, (node) => {
                    if (node.isText) {
                        node.marks.forEach(mark => {
                            if (!activeMarks.some(m => m.type.name === mark.type.name)) {
                                activeMarks.push(mark)
                            }
                        })
                    }
                })
            } else {
                activeMarks.push(...$from.marks())
            }

            activeMarks.forEach(mark => {
                const name = tagMap[mark.type.name] || mark.type.name
                const range = getMarkRange($from, mark.type)
                trail.push({name, pos: range.from, type: 'mark', size: range.to - range.from})
            })
        }
    }

    return trail.length > 1 ? trail : []
})

// --- Methods ---

const getMarkRange = ($pos, type) => {
    if (!$pos || !type) return {from: 0, to: 0}

    const {from} = props.editor.state.selection
    const doc = props.editor.state.doc
    const nodeAtFrom = doc.nodeAt(from)
    const hasMark = type.isInSet(nodeAtFrom?.marks || $pos.marks())

    if (hasMark) {
        let start = from
        let end = from

        while (start > 0) {
            const prevNode = doc.nodeAt(start - 1)
            if (prevNode && type.isInSet(prevNode.marks)) {
                start--
            } else {
                break
            }
        }

        while (end < doc.content.size) {
            const nextNode = doc.nodeAt(end)
            if (nextNode && type.isInSet(nextNode.marks)) {
                end++
            } else {
                break
            }
        }

        return {from: start, to: end}
    }

    return {from: $pos.start(), to: $pos.end()}
}

const selectCrumb = (crumb) => {
    const editor = props.editor

    if (crumb.name === 'body') {
        editor.commands.setTextSelection({
            from: 0,
            to: editor.state.doc.content.size
        })
    } else if (crumb.type === 'node') {
        editor.commands.setNodeSelection(crumb.pos)
    } else {
        editor.commands.setTextSelection({
            from: crumb.pos,
            to: crumb.pos + crumb.size
        })
    }
    editor.chain().focus().run()
}
</script>

<template>
    <div v-if="breadcrumbs.length" class="editor-footer px-2 py-1 border-top rounded rounded-top-0 small"
         :class="{'bg-light': !isDarkMode}">
        <nav aria-label="breadcrumb" style="--bs-breadcrumb-divider: ''">
            <ol class="breadcrumb mb-0">
                <li v-for="(crumb, index) in breadcrumbs" :key="index" class="breadcrumb-item small"
                    :class="{'pe-2': index === 0}"
                    @click="selectCrumb(crumb)" role="button">
                    {{ crumb.name }}
                </li>
            </ol>
        </nav>
    </div>
</template>